/**
 * DL files — Figma `04/ Work/ Record`, dropzone **12:5281**, table **12:5586**.
 *
 * ===========================================================================
 * THE UPLOAD GATE IS `work.files.manage`, NOT `work.update` (ESC-19, owner-ruled)
 * ===========================================================================
 * Attaching the artefact and REASSIGNING the work are different capabilities.
 * The Coordinator chases the DL file and must be able to attach it without
 * holding the power to move the job to a different developer; the assignee needs
 * to upload their own build without holding `crf.edit`. So the API gates upload
 * on its own key and this panel reads THAT key — `work.update` appears nowhere in
 * this file, and a Tech Support user (who holds neither) sees the list and no
 * dropzone.
 *
 * D5: the dropzone is ABSENT for a caller without the key, not disabled. A
 * greyed-out dropzone reads as a broken screen and invites a support call; an
 * absent one is the truth about their authority, and the list above it still
 * tells them what has been attached.
 *
 * ===========================================================================
 * `mime` IS NULL FOR A TDL, AND THE TABLE SAYS SO RATHER THAN GUESSING (ESC-14)
 * ===========================================================================
 * The API stores no MIME type for a `.tdl` because there is no registered one and
 * inventing `application/x-tdl` would be a fact nobody could rely on. This table
 * renders the extension it can see and leaves the type column honest — "—", not a
 * made-up string.
 *
 * ===========================================================================
 * DOWNLOAD IS `crf.view`, THE SAME KEY AS THE LIST (OQ-WF-DOWNLOAD, owner-ruled)
 * ===========================================================================
 * This panel first shipped listing files it could not retrieve, and said so. The
 * owner ruled that attach-and-list without retrieve is half a feature, so
 * `GET /work-orders/:id/files/:fileId` streams the bytes behind the JWT guard and
 * every row is now a download.
 *
 * The same key as the list, deliberately: knowing a DL file exists and being able
 * to open it are one fact for somebody who may see the work. Uploading stays
 * `work.files.manage` — that is the capability ESC-19 separated, and it is
 * untouched.
 *
 * A BUTTON, never an `<a href>`. An anchor carries no `Authorization` header, so
 * it would 401 and read as a link that does nothing at all. See `FileTable`.
 */

import { useRef, useState } from 'react';
import { Paperclip, Upload } from 'lucide-react';

import { Alert } from '../../components/ui';
import { WORK_FILE_ACCEPT, WORK_FILE_MAX_BYTES, downloadWorkFile } from './api';
import type { WorkFileDto } from './api';
import { useUploadWorkFile, useWorkFiles } from './use-work';
import { workErrorText } from './errors';

/**
 * Bytes as an operator reads them.
 *
 * Deliberately NOT `toLocaleString` with a unit: the cap this panel enforces is
 * expressed in MB in the design and in the API's own error, so the two must agree
 * on what "MB" means. 1 MB = 1024 × 1024, the same arithmetic as
 * `WORK_FILE_MAX_BYTES`.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

/** The extension, uppercased — the only type fact that is always true. */
export function fileKind(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot === -1 || dot === fileName.length - 1) return '—';
  return fileName.slice(dot + 1).toUpperCase();
}

export function WorkFilesPanel({
  workOrderId,
  canView,
  canUpload,
}: {
  workOrderId: string;
  canView: boolean;
  canUpload: boolean;
}): React.JSX.Element {
  const files = useWorkFiles(workOrderId, canView);
  const upload = useUploadWorkFile(workOrderId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  /**
   * A refusal this panel made ITSELF, before any request — currently only the
   * size cap. Kept separate from `upload.error` so the two cannot be confused:
   * one means "we did not send this", the other means "the server refused it".
   */
  const [localRefusal, setLocalRefusal] = useState<string | null>(null);
  /** A failed DOWNLOAD, kept apart from a failed upload: different verbs, different fixes. */
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const send = (file: File | undefined): void => {
    if (file === undefined) return;
    setLocalRefusal(null);
    if (file.size > WORK_FILE_MAX_BYTES) {
      // Refused here rather than sent-and-refused: a 10 MB cap enforced only by
      // the server means the operator waits for an upload that was never going to
      // be accepted. The server still enforces it — this is a courtesy, not the
      // guarantee.
      setLocalRefusal(
        `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(WORK_FILE_MAX_BYTES)}, so it was not sent.`,
      );
      return;
    }
    upload.mutate(file);
  };

  return (
    <section
      aria-labelledby="work-files-heading"
      className="rounded-md border border-line bg-surface"
    >
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 id="work-files-heading" className="text-body font-medium text-fg">
          DL files
        </h2>
        <p className="text-label text-fg-muted">
          {files.isPending
            ? 'Loading…'
            : files.isError
              ? 'Not read'
              : `${String(files.data?.length ?? 0)} attached`}
        </p>
      </header>

      <div className="flex flex-col gap-3 p-4">
        {canUpload ? (
          <>
            <div
              // A dropzone is a drop target, not a control: the BUTTON inside it is
              // what keyboard and screen-reader users operate, which is why this
              // div carries no role and no tabIndex.
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                send(event.dataTransfer.files[0]);
              }}
              data-testid="work-files-dropzone"
              className={`flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-6 text-center ${
                dragging ? 'border-indigo bg-subtle' : 'border-line-strong bg-subtle/40'
              }`}
            >
              <Upload aria-hidden="true" className="size-5 text-fg-muted" />
              <p className="text-body13 text-fg">
                Drop a TDL, PDF or ZIP here — up to {formatBytes(WORK_FILE_MAX_BYTES)}.
              </p>
              <button
                type="button"
                disabled={upload.isPending}
                onClick={() => inputRef.current?.click()}
                className="rounded-sm border border-line bg-surface px-2.5 py-1 text-body13 font-medium text-fg hover:border-line-strong disabled:opacity-60"
              >
                {upload.isPending ? 'Uploading…' : 'Choose a file'}
              </button>
              <input
                ref={inputRef}
                type="file"
                accept={WORK_FILE_ACCEPT}
                className="hidden"
                onChange={(event) => {
                  send(event.target.files?.[0]);
                  // Cleared so choosing the SAME file twice fires `change` again —
                  // a retry after a failed upload must not be silently ignored.
                  event.target.value = '';
                }}
              />
            </div>

            {localRefusal === null ? null : (
              <Alert tone="warning" title="That file was not sent">
                {localRefusal}
              </Alert>
            )}
            {upload.isError ? (
              <Alert tone="danger" title="The upload was refused">
                {workErrorText(upload.error)}
              </Alert>
            ) : null}
          </>
        ) : null}

        {files.isPending ? (
          <p className="text-body13 text-fg-muted">Loading the attached files…</p>
        ) : files.isError ? (
          // NOT an empty table. "The list could not be read" is not "nothing is
          // attached", and rendering zero rows for a failed request would tell the
          // operator the developer never uploaded anything.
          <Alert tone="danger" title="The file list could not be read">
            {workErrorText(files.error)} Nothing below is a count of what is attached.
          </Alert>
        ) : (files.data ?? []).length === 0 ? (
          <p className="text-body13 text-fg-muted">
            Nothing attached yet.
            {canUpload ? '' : ' Attaching files is not part of your access.'}
          </p>
        ) : (
          <FileTable
            files={files.data ?? []}
            workOrderId={workOrderId}
            onFailure={(text) => setDownloadError(text)}
          />
        )}

        {downloadError === null ? null : (
          <Alert tone="danger" title="That file could not be downloaded">
            {downloadError}
          </Alert>
        )}
      </div>
    </section>
  );
}

/**
 * The rows, each a DOWNLOAD (OQ-WF-DOWNLOAD, owner-ruled).
 *
 * A `<button>`, not an `<a href>`: the route is behind the JWT guard, and an
 * anchor sends no `Authorization` header — it would 401 and render as a download
 * that silently does nothing. The bytes come through the authenticated client and
 * are handed to the browser as an object URL, which is revoked immediately after
 * the click so a long-lived page does not accumulate blobs of customer files.
 */
function FileTable({
  files,
  workOrderId,
  onFailure,
}: {
  files: readonly WorkFileDto[];
  workOrderId: string;
  onFailure: (text: string) => void;
}): React.JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null);

  const download = async (file: WorkFileDto): Promise<void> => {
    setBusyId(file.id);
    try {
      const result = await downloadWorkFile(workOrderId, file.id);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      // The server's name wins; the row's is the fallback. Neither is a path.
      anchor.download = result.filename ?? file.file_name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      // RULE 11: a download that fails silently is the worst version of this —
      // the operator concludes the file is gone.
      onFailure(workErrorText(error));
    } finally {
      setBusyId(null);
    }
  };

  return <FileRows files={files} busyId={busyId} onDownload={download} />;
}

function FileRows({
  files,
  busyId,
  onDownload,
}: {
  files: readonly WorkFileDto[];
  busyId: string | null;
  onDownload: (file: WorkFileDto) => void;
}): React.JSX.Element {
  return (
    <table className="w-full text-left text-body13">
      <thead>
        <tr className="border-b border-line text-label text-fg-muted">
          <th scope="col" className="py-1.5 font-medium">
            File
          </th>
          <th scope="col" className="py-1.5 font-medium">
            Type
          </th>
          <th scope="col" className="py-1.5 text-right font-medium">
            Size
          </th>
          <th scope="col" className="py-1.5 text-right font-medium">
            Attached
          </th>
        </tr>
      </thead>
      <tbody>
        {files.map((file) => (
          <tr key={file.id} className="border-b border-line last:border-0">
            <td className="py-2 text-fg">
              <button
                type="button"
                disabled={busyId === file.id}
                onClick={() => onDownload(file)}
                className="flex min-w-0 items-center gap-1.5 text-left underline underline-offset-2 hover:decoration-2 disabled:opacity-60"
              >
                <Paperclip aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
                <span className="truncate">{file.file_name}</span>
                {busyId === file.id ? (
                  <span className="text-label text-fg-muted">downloading…</span>
                ) : null}
              </button>
            </td>
            {/* ESC-14: the extension is a fact; a MIME type for a TDL is not. */}
            <td className="py-2 text-fg-muted">{fileKind(file.file_name)}</td>
            <td className="tnum py-2 text-right text-fg-muted">
              {formatBytes(file.size_bytes)}
            </td>
            <td className="tnum py-2 text-right text-fg-muted">
              {new Date(file.created_at).toLocaleDateString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
